import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_PREFERENCES,
  EMPTY_PREFERENCES_DOC,
  mergePreferences,
  preferencesDocsEqual,
  setPreference as setPreferenceField,
  type PreferenceKey,
  type PreferencesDoc,
  type PreferencesStore,
  type TerminalPreferences,
} from '@paradigm/terminal-core';

import { createSafeContext } from '../context/createSafeContext';

/**
 * Settings state for the terminal.
 *
 * Deliberately mounted ABOVE `AgentProvider`: a preference is an input to the
 * agent (which model a new session starts on), not a product of it, and the
 * settings screen has to work before — and while — the gateway is unreachable.
 *
 * The write path is local-first. `setPreference` updates state and localStorage
 * synchronously, so the UI never waits on a round-trip and an offline change is
 * never lost, then announces the edit on `onLocalEdit` — which is how
 * `AgentProvider` knows to queue an upload. Remote changes arrive the other
 * way, via `adoptDoc`: after hydration merges the `ui-state` document, and
 * after the sync client resolves a write that lost a cross-device race.
 *
 * The asymmetry is the point. Only one of those two entry points is a user
 * edit, and it says so itself rather than leaving the uploader to work it out
 * by watching `doc` and diffing — which is what it used to do, and which meant
 * three separate guards to stop an arriving document being sent straight back.
 */
export interface PreferencesState {
  /** Current effective values — what the UI renders and the agent reads. */
  readonly preferences: TerminalPreferences;
  /**
   * The stamped envelope. Exposed (rather than kept private) because the
   * uploader must send stamps, not just values — they are what makes the
   * cross-device merge field-level.
   */
  readonly doc: PreferencesDoc;
  /**
   * False until the account's stored doc has been read. The settings UI waits
   * on this so controls don't paint a default and then visibly snap to the
   * stored value.
   */
  readonly loaded: boolean;
}

export interface PreferencesActions {
  /** Write one field, stamping it now. Persists locally, then syncs. */
  readonly setPreference: <K extends PreferenceKey>(
    key: K,
    value: TerminalPreferences[K],
  ) => void;
  /**
   * Replace the whole doc with an already-merged one — from sync hydration, or
   * from a conflict the sync client resolved mid-session. Not a user edit:
   * stamps come from the merge, so this must NOT re-stamp, or every load would
   * make this device's copy look like the newest write.
   */
  readonly adoptDoc: (doc: PreferencesDoc) => void;
  /**
   * Subscribe to LOCAL EDITS — and only those. Returns an unsubscribe.
   *
   * This exists so the uploader doesn't have to work out what an edit was.
   * `setPreference` is the only local mutator in the app, so the answer is
   * known at the call site; the alternative was inferring it downstream by
   * diffing each new doc against a "last known synced" baseline, which needed
   * that baseline seeded on load, reset on account switch, and re-recorded
   * every time a doc arrived from the gateway — four coupled parts, each a
   * chance to mistake an arrival for an edit and PUT a document the gateway
   * had just handed us.
   *
   * A doc that merely ARRIVES — read off disk at startup, merged in by
   * hydration, or resolved from a 412 — never reaches this listener, so those
   * cases need no guarding at all rather than needing three.
   *
   * One listener at a time. The uploader is a singleton and a second
   * subscriber would mean two writers racing on one document.
   *
   * The edited KEY comes with the doc so the uploader can tell a synced field
   * from a device-local one without diffing.
   */
  readonly onLocalEdit: (
    listener: (doc: PreferencesDoc, key: PreferenceKey) => void,
  ) => () => void;
}

export const [usePreferencesState, PreferencesStateProvider] =
  createSafeContext<PreferencesState>('PreferencesState');
export const [usePreferencesActions, PreferencesActionsProvider] =
  createSafeContext<PreferencesActions>('PreferencesActions');

interface Props {
  readonly children: ReactNode;
  readonly store: PreferencesStore;
  /** Account scope for the stored doc. `null` ⇒ signed out: defaults, no I/O. */
  readonly accountId: string | null;
}

export function PreferencesProvider({ children, store, accountId }: Props) {
  const [doc, setDoc] = useState<PreferencesDoc>(EMPTY_PREFERENCES_DOC);
  const [loaded, setLoaded] = useState(false);
  const [loadedAccount, setLoadedAccount] = useState<string | null>(null);

  // Swap accounts DURING RENDER, not in an effect.
  //
  // This used to be a `useEffect` keyed on `accountId`, which is one render too
  // late: the commit that changes `accountId` renders first, and it renders the
  // OUTGOING account's doc — with `loaded` still true, since a switch between
  // two signed-in accounts never passes through the signed-out branch. So the
  // settings screen painted one account's theme, language and default model
  // under the other's name before the effect corrected it, and anything reading
  // `preferences` in that window (the seed path reads the default model) saw
  // the wrong account's value.
  //
  // Adjusting state while rendering is React's own answer for "derive state
  // from a prop that changed": the component re-renders immediately with the
  // new state and nothing between is committed, so there is no window at all.
  // The read is a synchronous `localStorage` get, safe to repeat — the store
  // is the source of truth, not this state. Not quite side-effect-free: on a
  // doc carrying a clamped (far-future) stamp, `load` also writes the repaired
  // doc back (see `createPreferencesStore`). That write is idempotent, so a
  // StrictMode double-render or a discarded render just repeats it harmlessly.
  if (accountId !== loadedAccount) {
    setLoadedAccount(accountId);
    setDoc(accountId == null ? EMPTY_PREFERENCES_DOC : store.load(accountId));
    setLoaded(accountId != null);
  }

  /**
   * The current doc, readable from a callback without making that callback
   * change identity on every edit. Mirrored on render like the other refs in
   * this tree, and advanced eagerly by the writers below so two writes in one
   * tick compose instead of the second dropping the first.
   */
  const docRef = useRef(doc);
  docRef.current = doc;

  const localEditListenerRef = useRef<
    ((doc: PreferencesDoc, key: PreferenceKey) => void) | null
  >(null);
  const onLocalEdit = useCallback(
    (
      listener: (doc: PreferencesDoc, key: PreferenceKey) => void,
    ): (() => void) => {
      localEditListenerRef.current = listener;
      return () => {
        // Only clear our own registration: under StrictMode the remount's
        // subscribe runs before the first effect's cleanup, and clearing
        // unconditionally would drop the listener that is actually live.
        if (localEditListenerRef.current === listener) {
          localEditListenerRef.current = null;
        }
      };
    },
    [],
  );

  const setPreference = useCallback(
    <K extends PreferenceKey>(key: K, value: TerminalPreferences[K]): void => {
      // Computed outside the state updater, not inside it. React may call an
      // updater twice (StrictMode) and is free to discard the result, so
      // persisting and notifying from in there would double-fire; here the
      // write happens exactly once, in the order it reads.
      const next = setPreferenceField(docRef.current, key, value, Date.now());
      docRef.current = next;
      if (accountId != null) store.save(accountId, next);
      setDoc(next);
      // Local, deliberate, and stamped now — the one thing the gateway needs
      // to hear about.
      localEditListenerRef.current?.(next, key);
    },
    [store, accountId],
  );

  const adoptDoc = useCallback((incoming: PreferencesDoc): void => {
    // MERGE against live state rather than replace it.
    //
    // The incoming doc was merged from the doc on DISK, never from React
    // state, and the two can legitimately differ: `store.save` swallows
    // quota and private-mode failures, so an edit can exist only in state.
    // Replacing then reverted that edit on the next hydrate — and, because
    // the uploader marks whatever it adopts as already-synced, nothing ever
    // re-sent it. The same applies to a 412 resolving while the user is
    // editing: the merged payload predates the edit.
    //
    // Merging is safe precisely because the merge is field-level LWW: a field
    // the user just touched carries the newer stamp and survives, while
    // everything the gateway knows better about still lands.
    //
    // Identity is preserved when nothing actually changed, so a hydration that
    // agreed with local state doesn't re-render the settings screen.
    //
    // Deliberately does NOT notify `onLocalEdit`: this doc arrived, it wasn't
    // typed. That is the whole reason the seam is a callback on the mutator
    // rather than a watcher on the doc.
    const merged = mergePreferences(docRef.current, incoming);
    if (preferencesDocsEqual(docRef.current, merged)) return;
    docRef.current = merged;
    setDoc(merged);
  }, []);

  const state = useMemo<PreferencesState>(
    () => ({
      // Signed out, or before the read lands, render the defaults rather than
      // a half-populated doc.
      preferences: loaded ? doc.values : DEFAULT_PREFERENCES,
      doc,
      loaded,
    }),
    [doc, loaded],
  );

  const actions = useMemo<PreferencesActions>(
    () => ({ setPreference, adoptDoc, onLocalEdit }),
    [setPreference, adoptDoc, onLocalEdit],
  );

  return (
    <PreferencesStateProvider value={state}>
      <PreferencesActionsProvider value={actions}>
        {children}
      </PreferencesActionsProvider>
    </PreferencesStateProvider>
  );
}
